import { z } from 'zod';
import { SourceMode, SourceReadiness } from './game.ts';
import { CameraAliasId, NormPoint, UtcInstant } from './primitives.ts';

/** STILL_IMAGE (snapshot) feeds can never drive line-crossing games. */
export const MediaType = z.enum(['VIDEO_FILE', 'VIDEO_HLS', 'VIDEO_WEBRTC', 'VIDEO_WEBCAM', 'STILL_IMAGE']);
export type MediaType = z.infer<typeof MediaType>;

export const SourceUse = z.enum(['DISPLAY', 'CV_PROCESSING', 'REDISTRIBUTION', 'COMMERCIAL', 'EVIDENCE_RETENTION']);
export type SourceUse = z.infer<typeof SourceUse>;

export const ClockTrust = z.enum(['TRUSTED', 'UNTRUSTED', 'UNKNOWN']);
export type ClockTrust = z.infer<typeof ClockTrust>;

export const AuthMode = z.enum(['NONE', 'API_KEY', 'MTLS', 'VPN', 'SIGNED_URL', 'LOCAL_FILE']);

export const PrivacyMaskConfig = z.object({
  faces: z.boolean(),
  plates: z.boolean(),
  /** Static polygons to blank (e.g. windows), normalised to the frame. */
  staticRegions: z.array(z.array(NormPoint).min(3)),
});

/**
 * One entry of the source registry. Server-side record: origin URLs and credentials live here and
 * are never sent to clients (see PublicSourceView).
 */
export const SourceRegistryEntry = z.object({
  sourceId: z.string().min(1).max(64),
  cameraAlias: CameraAliasId,
  displayName: z.string().min(1).max(128),
  owner: z.string().min(1).max(128),
  officialCatalogueUrl: z.string().url().nullable(),
  mediaType: MediaType,
  /** Server-only. For LOCAL_FILE a path relative to the media root. */
  origin: z.string().min(1).max(1024).nullable(),
  permittedUses: z.array(SourceUse),
  licence: z.string().min(1).max(256),
  licenceEvidence: z.string().min(1).max(2048),
  attribution: z.string().max(512).nullable(),
  rightsExpiresAt: UtcInstant.nullable(),
  authMode: AuthMode,
  expectedFps: z.number().positive().nullable(),
  captureClockTrust: ClockTrust,
  captureErrorBoundMs: z.number().int().nonnegative().nullable(),
  maxSourceAgeMs: z.number().int().positive().nullable(),
  readiness: SourceReadiness,
  /** How pictures relate to reality when this source plays. */
  sourceMode: SourceMode,
  privacyMaskConfig: PrivacyMaskConfig,
  /** Measurements actually made; null = never measured. Unmeasured is never "tested". */
  measured: z
    .object({
      playedAt: UtcInstant,
      fps: z.number().positive().nullable(),
      width: z.number().int().positive().nullable(),
      height: z.number().int().positive().nullable(),
      latencyMs: z.number().int().nonnegative().nullable(),
      notes: z.string().max(1024),
    })
    .nullable(),
  notes: z.string().max(2048),
});
export type SourceRegistryEntry = z.infer<typeof SourceRegistryEntry>;

/** What a player client may know about a source: no origin, no credentials. */
export const PublicSourceView = SourceRegistryEntry.pick({
  sourceId: true,
  cameraAlias: true,
  displayName: true,
  owner: true,
  mediaType: true,
  licence: true,
  attribution: true,
  readiness: true,
  sourceMode: true,
  captureClockTrust: true,
  expectedFps: true,
  maxSourceAgeMs: true,
}).extend({
  /** Server-issued playback path (same-origin), or null when not playable. */
  playbackPath: z.string().max(512).nullable(),
  playbackKind: z.enum(['file', 'hls']).nullable(),
  selectable: z.boolean(),
  blockedReasons: z.array(z.string().max(64)),
  /** Uncalibrated preview geometry (calibration tool arrives in P04); null when none exists. */
  previewGeometry: z
    .object({
      roi: z.array(NormPoint).min(3),
      line: z.object({ lineId: z.string(), a: NormPoint, b: NormPoint, countedDirection: z.enum(['A_TO_B', 'B_TO_A']) }),
    })
    .nullable(),
});
export type PublicSourceView = z.infer<typeof PublicSourceView>;
